/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import axios from "axios";
import get from "lodash/fp/get";
import merge from "lodash/fp/merge";

export interface ReportErrorOptions {
  sendToServer: boolean;
  extras: Record<string, string>;
}

const defaultReportErrorOptions: ReportErrorOptions = {
  sendToServer: false,
  extras: {},
};

interface ErrorLike {
  name: string;
  message: string;
  stack: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isErrorLike = (val: any): val is ErrorLike => {
  const keys = ["name", "message", "stack"];
  return !keys.find((key) => !get(key, val));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sendToServer = async (error: any, extras: Record<string, string>) => {
  const errorPayload = (() => {
    if (isErrorLike(error)) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return String(error);
  })();

  await axios.post("/v1/log/error", {
    ...extras,
    error: errorPayload,
  });
};

// Generic error log proxy, error can be "any" as these can come from 3rd party scripts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reportError = (error: any, options?: Partial<ReportErrorOptions>) => {
  // Add log functionality here when available
  // eslint-disable-next-line no-console
  console.error(error);

  if (options) {
    // eslint-disable-next-line no-console
    console.log(options);
  }

  const mergedOptions = merge(defaultReportErrorOptions, options ?? {});

  if (mergedOptions.sendToServer) {
    // eslint-disable-next-line no-console
    sendToServer(error, mergedOptions.extras).catch(console.error);
  }
};

export default reportError;
